/**
 * The `accept` vocabulary the platform's `<input type="file" accept>` defines,
 * applied to files that arrive by drop and paste too — where the platform
 * applies nothing at all.
 *
 * One implementation for the whole library: `<schmancy-dropzone>` and
 * `<schmancy-attachments>` both decide acceptance here, so a file refused by
 * one is refused by the other for the same reason and in the same words.
 */
import { componentText } from '@schmancy/mixins/index'

/** `accept` split into the comparable patterns it names. An empty list accepts everything. */
function patternsOf(accept: string): string[] {
	return accept
		.split(',')
		.map(pattern => pattern.trim().toLowerCase())
		.filter(Boolean)
}

/**
 * Whether `accept` admits this file. Matches the platform's own three pattern
 * forms — `*​/*`, a `type/*` family, an exact MIME type, and a `.ext` suffix —
 * against the file's MIME type, falling back to its name only for the `.ext`
 * form, which is the one form the MIME type cannot answer.
 */
export function acceptsFile(accept: string, file: File): boolean {
	const patterns = patternsOf(accept)
	if (patterns.length === 0) return true
	const type = file.type.toLowerCase()
	const named = file.name.toLowerCase()
	return patterns.some(pattern => {
		if (pattern === '*/*') return true
		if (pattern.endsWith('/*')) return type.startsWith(pattern.slice(0, -1))
		if (pattern.startsWith('.')) return named.endsWith(pattern)
		return type === pattern
	})
}

/**
 * The refusal a rejected file earns, phrased from what `accept` actually asks
 * for. A single wildcard family is a thing the reader has a word for — "an
 * image", "a video" — so it is named; anything else can only be quoted back,
 * because "not one of image/png, .pdf" has no shorter true form in the
 * library's own English.
 *
 * Which is why the sentences live in `componentTextDefaults` and not here. A
 * quoted MIME list is the machinery's vocabulary, and it is the one thing a
 * refusal must not be: the reader dropped a photo and is owed a sentence about
 * the photo. `dropzone.refusal.type` therefore owns its WHOLE sentence, so an
 * app that knows its own `accept` in words is free to drop `{{accept}}` and say
 * "is not a JPG or PNG" — in whatever language its reader reads.
 */
export function acceptRefusal(accept: string, file: File): string {
	const patterns = patternsOf(accept)
	const families = new Set(patterns.filter(pattern => pattern.endsWith('/*')).map(pattern => pattern.slice(0, -2)))
	if (families.size === 1 && families.size === patterns.length) {
		const [family] = [...families]
		if (family === 'image') return componentText('dropzone.refusal.image', { name: file.name })
		if (family === 'video') return componentText('dropzone.refusal.video', { name: file.name })
		if (family === 'audio') return componentText('dropzone.refusal.audio', { name: file.name })
		if (family === 'text') return componentText('dropzone.refusal.text', { name: file.name })
	}
	return componentText('dropzone.refusal.type', {
		name: file.name,
		// `.pdf` and `application/pdf` are one format written twice, so both reduce
		// to the one word a reader has for it and the Set drops the duplicate. A
		// `type/*` family inside a mixed list is already a human category and stays
		// whole; `*/*` cannot reach a refusal. The separator is a comma rather than
		// a conjunction because the sentence around it is the translation's, and a
		// list joined in English inside a German sentence is the defect one level in.
		formats: [
			...new Set(
				patterns.map(pattern => {
					if (pattern.startsWith('.')) return pattern.slice(1).toUpperCase()
					if (pattern.endsWith('/*')) return pattern
					const subtype = pattern.split('/')[1] ?? pattern
					return (subtype.split('+')[0] ?? subtype).toUpperCase()
				}),
			),
		].join(', '),
	})
}

/**
 * Why a file did not get in — the axis an app teaches along, because the words
 * it wants to add are cause-specific and the control's own notice is one
 * sentence written for everyone.
 *
 * - `type` — `accept` does not admit the file.
 * - `tooLarge` — admitted, and past the control's byte ceiling.
 * - `tooMany` — admitted, and the control is already holding all it will hold.
 * - `unreadable` — admitted, and the browser could not decode it. A HEIC lands
 *   here rather than on `type` wherever `accept` is `image/*`, because
 *   `image/heic` IS a member of the family the accept grammar admits and only
 *   the decoder knows better; a control that never decodes anything never
 *   reaches this cause.
 */
export type FileRefusalCause = 'type' | 'tooLarge' | 'tooMany' | 'unreadable'

export interface FileRefusal {
	/** The file itself, so an app can discriminate finer than `cause` does — by suffix, by MIME, by size. */
	file: File
	cause: FileRefusalCause
	/** What the control is saying about it on screen, in the control's own words. */
	message: string
}

/** `@fires refusal` on `<schmancy-dropzone>` and `<schmancy-attachments>` alike. */
export type SchmancyFileRefusalEvent = CustomEvent<FileRefusal>
